import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faEye, faUsers, faBox, faSync, faTrash, 
    faSearch, faChartLine, faLayerGroup, faHistory, faSitemap,
    faUserShield, faChartBar, faGlobe, faMobileAlt, faDesktop, 
    faCalendarAlt, faFilter, faMapMarkerAlt 
} from '@fortawesome/free-solid-svg-icons';
import { faFacebook, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { API_BASE } from '../apiConfig';

const AdminAnalytics = () => {
    const { t } = useTranslation();
    const [analytics, setAnalytics] = useState({ totalVisits: 0, uniqueUsers: 0, totalOrders: 0 });
    const [categories, setCategories] = useState([]); 
    const [subservices, setSubservices] = useState([]); 
    const [searchLogs, setSearchLogs] = useState([]);
    const [visitorLogs, setVisitorLogs] = useState([]); // Իրական այցելությունների լոգերը
    const [searchFilter, setSearchFilter] = useState('');
    const [loading, setLoading] = useState(true);
const [ipLocations, setIpLocations] = useState({});
    const requestOptions = useMemo(() => ({
        headers: { 
            'ngrok-skip-browser-warning': 'true', 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    }), []);
const getDeviceIcon = (ua) => {
    if (!ua) return { icon: faGlobe, color: "#A3AED0" }; // Եթե տվյալ չկա
    
    const uaLower = ua.toLowerCase();
    
    // 1. Ստուգում ենք՝ արդյոք հավելվածից է (եթե ձեր հավելվածում ավելացրել եք հատուկ բառ)
    if (uaLower.includes('myserviceapp')) {
        return { icon: faMobileAlt, color: "#01B574" }; // Հավելվածի համար կանաչ գույն
    }
    
    // 2. Պլանշետներ
    if (/tablet|ipad|playbook|silk/i.test(uaLower)) {
        return { icon: faMobileAlt, color: "#FF9933" }; 
    }
    
    // 3. Հեռախոսներ
    if (/mobile|android|iphone|ipod/i.test(uaLower)) {
        return { icon: faMobileAlt, color: "#4318FF" }; 
    }
    
    // 4. Համակարգիչ
    return { icon: faDesktop, color: "#1B2559" };
};
const fetchCity = async (ip) => {
    try {
        // Եթե IP-ն տեղային է (localhost), API-ն սխալ կտա
        if (ip === '::1' || ip === '127.0.0.1') return 'Localhost';
        
        const response = await fetch(`http://ip-api.com/json/${ip}?fields=city,country`);
        const data = await response.json();
        return data.status === 'success' ? `${data.city}, ${data.country}` : 'Unknown';
    } catch {
        return 'Error';
    }
};
  const loadAnalyticsData = useCallback(async () => {
    setLoading(true);
    try {
        const [resStats, resCats, resSubs, resLogs, resVisitorLogs] = await Promise.all([
            fetch(`${API_BASE}/api/admin/analytics`, requestOptions), // Այստեղ stats-ը փոխեցինք admin
            fetch(`${API_BASE}/api/categories`, requestOptions),
            fetch(`${API_BASE}/api/subservices`, requestOptions),
            fetch(`${API_BASE}/api/stats/search-logs`, requestOptions), // Սա թող մնա stats, եթե հինն է
            fetch(`${API_BASE}/api/admin/visitor-logs`, requestOptions) // Սա էլ admin
        ]);

        const statsJson = await resStats.json();
        const cats = await resCats.json();
        const subs = await resSubs.json();
        const logs = await resLogs.json();
        const vLogs = await resVisitorLogs.json();

        // ԳԼԽԱՎՈՐ ՈՒՂՂՈՒՄԸ ՍԱ Է
        if (statsJson.success && statsJson.data) {
            // Մեր Backend-ը ուղարկում է { totalOrders, totalUsers, analytics: { totalVisits, uniqueVisitors } }
            setAnalytics({
                totalVisits: statsJson.data.analytics?.totalVisits || 0,
                uniqueUsers: statsJson.data.analytics?.uniqueVisitors || 0,
                totalOrders: statsJson.data.totalOrders || 0
            });
        }

        setCategories(Array.isArray(cats) ? cats : []);
        setSubservices(Array.isArray(subs) ? subs : []);
        setSearchLogs(Array.isArray(logs) ? logs : []);
        setVisitorLogs(Array.isArray(vLogs) ? vLogs : []);

    } catch (e) {
        console.error("Analytics fetch error:", e);
    } finally {
        setLoading(false);
    }
}, [requestOptions]);

    useEffect(() => { loadAnalyticsData(); }, [loadAnalyticsData]);

    // --- ՏՎՅԱԼՆԵՐԻ ՎԵՐԱՄՇԱԿՈՒՄ (LOGIC) ---

    const dailyStats = useMemo(() => {
        const stats = visitorLogs.reduce((acc, log) => {
            const date = log.visit_time?.split(' ')[0] || 'Unknown';
            acc[date] = (acc[date] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(stats).sort((a, b) => new Date(b[0]) - new Date(a[0])).slice(0, 7);
    }, [visitorLogs]);

    const sourceStats = useMemo(() => {
        const stats = { facebook: 0, google: 0, direct: 0 };
        visitorLogs.forEach(log => {
            if (!log.referrer) stats.direct++;
            else if (log.referrer.includes('facebook')) stats.facebook++;
            else if (log.referrer.includes('google')) stats.google++;
            else stats.direct++;
        });
        return stats;
    }, [visitorLogs]);

    const ipAnalysis = useMemo(() => {
        const counts = visitorLogs.reduce((acc, log) => {
            acc[log.ip_address] = (acc[log.ip_address] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6);
    }, [visitorLogs]);

    const topCategories = useMemo(() => 
        [...categories].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 6), 
    [categories]);

    const topSubservices = useMemo(() => 
        [...subservices].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 6), 
    [subservices]);

    const groupedSearches = useMemo(() => {
        const groups = (searchLogs || []).reduce((acc, log) => {
            const query = log.query ? log.query.trim().toLowerCase() : '';
            if (!query) return acc;
            if (!acc[query]) acc[query] = { query: log.query, count: 0, lastId: log.id };
            acc[query].count += 1;
            return acc;
        }, {});
        return Object.values(groups)
            .filter(l => l.query.includes(searchFilter.toLowerCase()))
            .sort((a, b) => b.count - a.count);
    }, [searchLogs, searchFilter]);

    const deleteLog = async (id) => {
        if (!window.confirm(t('delete_confirm'))) return;
        try {
            const res = await fetch(`${API_BASE}/api/stats/search-logs/${id}`, { method: 'DELETE', ...requestOptions });
            if (res.ok) setSearchLogs(prev => prev.filter(log => log.id !== id));
        } catch (error) { console.error(error); }
    };
useEffect(() => {
    const resolveIps = async () => {
        const uniqueIps = [...new Set(visitorLogs.map(log => log.ip_address))];
        const locations = { ...ipLocations };

        for (const ip of uniqueIps) {
            if (!locations[ip]) { // Եթե դեռ չենք գտել այս IP-ն
                locations[ip] = await fetchCity(ip);
            }
        }
        setIpLocations(locations);
    };

    if (visitorLogs.length > 0) resolveIps();
}, [visitorLogs]);
    return (
        <div style={styles.dashboard}>
            <style>{`
                .ana-main-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                .ana-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
                .col-name { width: 70%; padding: 12px 5px; word-wrap: break-word; overflow-wrap: anywhere; white-space: normal; }
                .col-views { width: 30%; padding: 12px 5px; text-align: right; vertical-align: middle; }
                .card { background: #fff; border-radius: 20px; padding: 20px; box-shadow: 0 10px 30px rgba(0,0,0,0.02); margin-bottom: 20px; border: 1px solid #E0E5F2; }
                @media (max-width: 1024px) { .ana-main-grid { grid-template-columns: 1fr; } }
            `}</style>

            {/* TOP ANALYTIC CARDS */}
            <div style={styles.analyticsRow}>
                <AnalyticCard title={t('total_visits')} value={analytics.totalVisits} icon={faEye} grad="linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)" />
                <AnalyticCard title={t('unique_users')} value={analytics.uniqueUsers} icon={faUsers} grad="linear-gradient(135deg, #01B574 0%, #2AF598 100%)" />
                <AnalyticCard title={t('total_orders')} value={analytics.totalOrders} icon={faBox} grad="linear-gradient(135deg, #FF9933 0%, #FFB366 100%)" />
            </div>

            {/* MARKETING SOURCES */}
            <div style={styles.statsRow}>
                <SourceCard title="Facebook" count={sourceStats.facebook} icon={faFacebook} color="#1877F2" />
                <SourceCard title="Google" count={sourceStats.google} icon={faGoogle} color="#DB4437" />
                <SourceCard title="Direct / Other" count={sourceStats.direct} icon={faGlobe} color="#4318FF" />
            </div>

            <div style={styles.mainHeader}>
                <h2 style={styles.title}><FontAwesomeIcon icon={faChartLine} style={{marginRight: '10px'}} /> {t('site_analytics')}</h2>
                <button onClick={loadAnalyticsData} style={styles.refreshCircle} disabled={loading}>
                    <FontAwesomeIcon icon={faSync} spin={loading} />
                </button>
            </div>

            <div className="ana-main-grid">
                
                {/* 1. DAILY STATS */}
                <div className="card">
                    <h3 style={styles.cardTitle}><FontAwesomeIcon icon={faChartBar} /> {t('daily_visits')}</h3>
                    <table className="ana-table">
                        <thead>
                            <tr>
                                <th style={styles.th}>Ամսաթիվ</th>
                                <th style={{...styles.th, textAlign:'right'}}>Այցելու</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dailyStats.map(([date, count]) => (
                                <tr key={date} style={styles.tr}>
                                    <td style={styles.tdName}>{date}</td>
                                    <td style={{textAlign:'right'}}><span style={styles.viewBadgeSub}>{count}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* 2. IP MONITORING */}
                <div className="card">
                    <h3 style={styles.cardTitle}><FontAwesomeIcon icon={faUserShield} /> {t('active_ips')}</h3>
                    <table className="ana-table">
                        <thead>
                            <tr>
                                <th style={styles.th}>IP Հասցե</th>
                                <th style={{...styles.th, textAlign:'right'}}>Քանակ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ipAnalysis.map(([ip, count]) => (
                                <tr key={ip} style={styles.tr}>
                                    <td style={{...styles.tdName, color: '#4318FF'}}>{ip}</td>
                                    <td style={{textAlign:'right'}}><span style={styles.viewBadge}>{count}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* 3. CATEGORIES */}
                <div className="card">
                    <h3 style={styles.cardTitle}><FontAwesomeIcon icon={faLayerGroup} /> {t('top_categories')}</h3>
                    <table className="ana-table">
                        <tbody>
                            {topCategories.map(cat => (
                                <tr key={cat.id} style={styles.tr}>
                                    <td className="col-name" style={styles.tdName}>{cat.name}</td>
                                    <td className="col-views"><span style={styles.viewBadge}>{cat.views || 0}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* 4. SUBSERVICES */}
                <div className="card">
                    <h3 style={styles.cardTitle}><FontAwesomeIcon icon={faSitemap} /> {t('top_subservices')}</h3>
                    <table className="ana-table">
                        <tbody>
                            {topSubservices.map(sub => (
                                <tr key={sub.id} style={styles.tr}>
                                    <td className="col-name" style={styles.tdName}>{sub.name}</td>
                                    <td className="col-views"><span style={styles.viewBadgeSub}>{sub.views || 0}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* 5. SEARCH LOGS */}
                <div className="card" style={{gridColumn: '1 / -1'}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px'}}>
                        <h3 style={styles.cardTitle}><FontAwesomeIcon icon={faSearch} /> {t('search_frequency')}</h3>
                        <input 
                            style={styles.smallSearch} 
                            placeholder={t('filter_placeholder')} 
                            value={searchFilter} 
                            onChange={e => setSearchFilter(e.target.value)} 
                        />
                    </div>
                    <div style={styles.historyListGrid}>
                        {groupedSearches.slice(0, 8).map((log) => (
                            <div key={log.query} style={styles.logItem}>
                                <div style={styles.logInfo}>
                                    <div style={styles.logIcon}><FontAwesomeIcon icon={faHistory} /></div>
                                    <div>
                                        <div style={styles.logQuery}>{log.query}</div>
                                        <div style={styles.logCount}>{log.count} {t('times')}</div>
                                    </div>
                                </div>
                                <button onClick={() => deleteLog(log.lastId)} style={styles.deleteBtn}><FontAwesomeIcon icon={faTrash} /></button>
                            </div>
                        ))}
                    </div>
                </div>

            {/* 6. FULL VISITOR LOG */}
<div className="card" style={{ gridColumn: '1 / -1' }}>
    <h3 style={styles.cardTitle}>
        <FontAwesomeIcon icon={faGlobe} /> {t('visit_logs')}
    </h3>
    <div style={{ overflowX: 'auto' }}>
        <table className="ana-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
                <tr>
                    <th style={{ ...styles.th, textAlign: 'left' }}>Ժամանակ</th>
                    <th style={{ ...styles.th, textAlign: 'left' }}>IP & Տեղանք</th>
                    <th style={{ ...styles.th, textAlign: 'left' }}>Աղբյուր</th>
                    <th style={{ ...styles.th, textAlign: 'left' }}>Էջ</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Սարք</th>
                </tr>
            </thead>
            <tbody>
                {visitorLogs.slice(0, 15).map((log) => {
                    // --- Դեվայսի որոշման լոգիկա ---
                    const ua = log.user_agent || "";
                    let deviceIcon = faDesktop;
                    let deviceColor = "#A3AED0";
                    let deviceName = "Desktop";

                    if (ua.includes('MyServiceApp')) {
                        deviceIcon = faMobileAlt;
                        deviceColor = "#01B574";
                        deviceName = "App";
                    } else if (/android/i.test(ua)) {
                        deviceIcon = faMobileAlt;
                        deviceColor = "#3DDC84";
                        deviceName = "Android";
                    } else if (/iphone|ipad|ipod/i.test(ua)) {
                        deviceIcon = faMobileAlt;
                        deviceColor = "#4318FF";
                        deviceName = "iOS";
                    }

                    return (
                        <tr key={log.id} style={styles.tr}>
                            {/* Ժամանակ */}
                            <td style={{ ...styles.tdName, fontSize: '12px', opacity: 0.7, minWidth: '140px' }}>
                                {new Date(log.visit_time).toLocaleString('hy-AM')}
                            </td>

                            {/* IP & Տեղանք (Քո բազայի location սյունակից) */}
                            <td style={{ ...styles.tdName, minWidth: '160px' }}>
                                <div style={{ color: '#4318FF', fontWeight: '500' }}>{log.ip_address}</div>
                                <div style={{ fontSize: '11px', color: '#718096', display: 'flex', alignItems: 'center', marginTop: '2px' }}>
                                    <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '4px', color: '#E31A1A', fontSize: '10px' }} />
                                    {log.location || "Unknown"}
                                </div>
                            </td>

                            {/* Աղբյուր (Referrer) */}
                            <td style={{ ...styles.tdName, minWidth: '120px' }}>
                                <span style={{ 
                                    ...styles.dirBadge, 
                                    maxWidth: '120px', 
                                    display: 'inline-block', 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap' 
                                }}>
                                    {log.referrer && log.referrer.includes('localhost') ? 'Localhost' : (log.referrer || 'Direct')}
                                </span>
                            </td>

                            {/* Էջը */}
                            <td style={{ ...styles.tdName, color: '#1B2559', fontWeight: '500' }}>
                                {log.path_name}
                            </td>

                            {/* Սարք / Պլատֆորմ */}
                            <td style={{ textAlign: 'center', minWidth: '80px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <FontAwesomeIcon 
                                        icon={deviceIcon} 
                                        style={{ color: deviceColor, fontSize: '16px' }} 
                                        title={ua} 
                                    />
                                    <span style={{ fontSize: '10px', color: '#A3AED0', marginTop: '2px' }}>
                                        {deviceName}
                                    </span>
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
</div>

            </div>
        </div>
    );
};

// Sub-components
const AnalyticCard = ({ title, value, icon, grad }) => (
    <div style={{...styles.anaCard, background: grad}}>
        <div style={styles.anaIconBox}><FontAwesomeIcon icon={icon} /></div>
        <div>
            <p style={styles.anaLabel}>{title}</p>
            <h3 style={styles.anaVal}>{value?.toLocaleString() || '0'}</h3>
        </div>
    </div>
);

const SourceCard = ({ title, count, icon, color }) => (
    <div style={styles.sourceCardMini}>
        <div style={{...styles.iconBoxMini, backgroundColor: color + '15'}}>
            <FontAwesomeIcon icon={icon} color={color} />
        </div>
        <div>
            <div style={{fontSize:'11px', color:'#A3AED0', fontWeight:'bold'}}>{title}</div>
            <div style={{fontSize:'16px', color:'#1B2559', fontWeight:'800'}}>{count}</div>
        </div>
    </div>
);

const styles = {
    dashboard: { padding: '20px', backgroundColor: '#F4F7FE', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif" },
    analyticsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '15px', marginBottom: '20px' },
    statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '25px' },
    anaCard: { padding: '20px', borderRadius: '20px', color: '#fff', display: 'flex', alignItems: 'center', gap: '15px' },
    anaIconBox: { minWidth: '45px', height: '45px', borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    anaLabel: { fontSize: '12px', margin: 0, opacity: 0.9 },
    anaVal: { fontSize: '22px', margin: 0, fontWeight: '800' },
    sourceCardMini: { background: '#fff', padding: '15px', borderRadius: '18px', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid #E0E5F2' },
    iconBoxMini: { width: '35px', height: '35px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    mainHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    title: { fontSize: '20px', fontWeight: '800', color: '#1B2559', margin: 0 },
    refreshCircle: { width: '35px', height: '35px', borderRadius: '50%', border: 'none', background: '#fff', color: '#4318FF', cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' },
    cardTitle: { fontSize: '15px', fontWeight: '800', color: '#1B2559', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' },
    th: { color: '#A3AED0', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', textAlign: 'left', paddingBottom: '10px' },
    tr: { borderBottom: '1px solid #F4F7FE' },
    tdName: { fontSize: '13px', fontWeight: '700', color: '#1B2559', padding: '10px 0' },
    viewBadge: { background: '#E9EDF7', color: '#4318FF', padding: '3px 8px', borderRadius: '6px', fontWeight: '800', fontSize: '11px' },
    viewBadgeSub: { background: '#E2F9EF', color: '#01B574', padding: '3px 8px', borderRadius: '6px', fontWeight: '800', fontSize: '11px' },
    dirBadge: { color: '#718096', background: '#EDF2F7', padding: '2px 6px', borderRadius: '5px', fontSize: '11px' },
    smallSearch: { padding: '8px 12px', borderRadius: '10px', border: '1px solid #E0E5F2', background: '#F4F7FE', outline: 'none', fontSize: '12px' },
    historyListGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' },
    logItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', borderRadius: '12px', background: '#F4F7FE' },
    logInfo: { display: 'flex', alignItems: 'center', gap: '10px' },
    logIcon: { width: '30px', height: '30px', background: '#fff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4318FF', fontSize: '12px' },
    logQuery: { fontSize: '12px', fontWeight: '700', color: '#1B2559' },
    logCount: { fontSize: '10px', color: '#A3AED0' },
    deleteBtn: { background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer' }
};

export default AdminAnalytics;